import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Mail, Phone, Smartphone, Linkedin, Building2, Star,
  Briefcase, ExternalLink, AlertTriangle, Edit3, Save,
} from 'lucide-react';
import api from '../api';
import { Contact } from '../types';

const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', mobile: '', jobTitle: '', isDecisionMaker: false, linkedinUrl: '',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchContact = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/contacts/${id}`);
      setContact(res.data);
      setForm({
        firstName: res.data.firstName || '',
        lastName: res.data.lastName || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        mobile: res.data.mobile || '',
        jobTitle: res.data.jobTitle || '',
        isDecisionMaker: res.data.isDecisionMaker || false,
        linkedinUrl: res.data.linkedinUrl || '',
      });
    } catch (err) {
      setError('Contact introuvable.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  const handleSave = async () => {
    setSaveError('');
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setSaveError('Le prénom et le nom sont obligatoires.');
      return;
    }
    setSaveLoading(true);
    try {
      const res = await api.patch(`/contacts/${id}`, form);
      setContact(res.data);
      setEditing(false);
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  if (error || !contact) {
    return (
      <div className="empty-state">
        <AlertTriangle size={48} />
        <p className="empty-state-title">{error || 'Contact introuvable'}</p>
        <button className="btn" onClick={() => navigate('/contacts')}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div>
      <div className="detail-header">
        <div>
          <h1>
            <User size={28} />
            {contact.firstName} {contact.lastName}
            {contact.isDecisionMaker && <span className="badge badge-purple"><Star size={12} /> Décideur</span>}
          </h1>
          {contact.jobTitle && <div className="text-secondary mt-1">{contact.jobTitle}</div>}
        </div>
        <div className="btn-group">
          {!editing ? (
            <button className="btn" onClick={() => setEditing(true)}>
              <Edit3 size={16} /> Modifier
            </button>
          ) : (
            <>
              <button className="btn" onClick={() => { setEditing(false); fetchContact(); }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saveLoading}>
                {saveLoading ? <span className="spinner spinner-sm" /> : <><Save size={16} /> Enregistrer</>}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && <div className="login-error mb-3">{saveError}</div>}

      <div className="two-col">
        <div className="card">
          <div className="card-header"><h3>Informations</h3></div>
          <div className="card-body">
            {editing ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Prénom *</label>
                    <input className="form-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nom *</label>
                    <input className="form-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Téléphone</label>
                    <input className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mobile</label>
                    <input className="form-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Poste</label>
                  <input className="form-input" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">LinkedIn</label>
                  <input className="form-input" value={form.linkedinUrl} onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-check">
                    <input type="checkbox" checked={form.isDecisionMaker} onChange={(e) => setForm({ ...form, isDecisionMaker: e.target.checked })} />
                    Décideur
                  </label>
                </div>
              </>
            ) : (
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-item-label">Email</div>
                  <div className="detail-item-value">
                    {contact.email ? <span className="inline-flex"><Mail size={14} /> {contact.email}</span> : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Téléphone</div>
                  <div className="detail-item-value">
                    {contact.phone ? <span className="inline-flex"><Phone size={14} /> {contact.phone}</span> : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Mobile</div>
                  <div className="detail-item-value">
                    {contact.mobile ? <span className="inline-flex"><Smartphone size={14} /> {contact.mobile}</span> : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Poste</div>
                  <div className="detail-item-value">
                    {contact.jobTitle ? <span className="inline-flex"><Briefcase size={14} /> {contact.jobTitle}</span> : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">LinkedIn</div>
                  <div className="detail-item-value">
                    {contact.linkedinUrl ? (
                      <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex">
                        <Linkedin size={14} /> Profil LinkedIn <ExternalLink size={12} />
                      </a>
                    ) : '-'}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-item-label">Décideur</div>
                  <div className="detail-item-value">
                    {contact.isDecisionMaker ? (
                      <span className="badge badge-purple"><Star size={12} /> Oui</span>
                    ) : 'Non'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Entreprise</h3></div>
          <div className="card-body">
            {contact.company ? (
              <div>
                <div
                  className="inline-flex cursor-pointer text-primary font-semibold"
                  onClick={() => navigate(`/companies/${contact.company!.id}`)}
                  style={{ fontSize: '1.1rem', marginBottom: '12px' }}
                >
                  <Building2 size={18} /> {contact.company.name}
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <div className="detail-item-label">SIREN</div>
                    <div className="detail-item-value">{contact.company.siren}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Ville</div>
                    <div className="detail-item-value">{contact.company.addressCity || '-'}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Secteur</div>
                    <div className="detail-item-value">{contact.company.nafLabel || contact.company.nafCode || '-'}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <Building2 size={36} />
                <p className="text-secondary">Aucune entreprise associée</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailPage;
